import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  CallRouteUsage,
  VoicemailBox,
} from '@weave/schema-gen-ts/dist/schemas/phone/voicemail-config/v2/voicemail_config.pb';
import { Trans } from 'react-i18next';
import { useQuery } from 'react-query';
import { VoicemailConfigAPI } from '@frontend/api-voicemail-boxes';
import { useTranslation } from '@frontend/i18n';
import { useInvalidateQueries } from '@frontend/location-helpers';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  Heading,
  Modal,
  ModalControlModalProps,
  ModalControlResponse,
  Text,
  TextField,
  TextLink,
  Tray,
  useAlert,
  useForm,
  useModalControl,
} from '@frontend/design-system';
import { useVoicemailCacheUpdater } from '../../hooks/useVoicemailCacheUpdater';
import { queryKeys } from '../../query-keys';
import { populateSchema, createUpdatePayload } from '../voicemail-box/new/schema';
import { useNotificationValidation } from '../voicemail-box/new/use-notification-validation';
import { useVoicemailBoxMutations } from '../voicemail-box/new/use-voicemail-box-mutations';
import { VoicemailBoxContent } from '../voicemail-box/new/voicemail-box-detail';

type Props = {
  addStepModalProps?: ModalControlModalProps;
  modalControls: ModalControlResponse;
  mountTarget: string;
  selectedVoicemailBox?: VoicemailBox;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
};

type ContentProps = {
  selectedVoicemailBox: VoicemailBox;
  modalControls: ModalControlResponse;
  addStepModalProps?: ModalControlModalProps;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
};

const VoicemailSettingTrayContent = ({
  selectedVoicemailBox,
  modalControls,
  addStepModalProps,
  stepContext,
}: ContentProps) => {
  const { t } = useTranslation('phone');
  const voicemailBoxId = selectedVoicemailBox.mailbox.id || '';
  const alert = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { modalProps: usageModalProps, triggerProps: usageTriggerProps } = useModalControl();
  const invalidateQueries = useInvalidateQueries();
  const { updateCallRouteCaches } = useVoicemailCacheUpdater(voicemailBoxId);

  const { data: usageData } = useQuery({
    queryKey: queryKeys.settings.listVoicemailBoxUsage(voicemailBoxId),
    queryFn: () => VoicemailConfigAPI.Usage({ voicemailBoxId }),
    select: (data) => data.callRouteUsages,
    staleTime: 0,
    enabled: !!voicemailBoxId,
  });

  const voicemailBoxData = useMemo(() => {
    return {
      ...selectedVoicemailBox,
      greetings: [],
      callObjectUsage: {},
    };
  }, [selectedVoicemailBox]);

  const populatedSchema = useMemo(() => {
    return populateSchema(voicemailBoxData);
  }, [voicemailBoxData]);

  const form = useForm({
    fields: populatedSchema,
    computeChangedValues: true,
  });

  const nameField = form.getFieldProps('name');

  const { notificationsEnabledWithoutContacts, saveButtonTriggerProps, NotificationTooltip } =
    useNotificationValidation(form);

  const { update } = useVoicemailBoxMutations(settingsTenantLocation?.phoneTenantId ?? '');

  const handleSave = useCallback(() => {
    const finalState = createUpdatePayload({
      state: voicemailBoxData,
      formValues: form.values,
    });

    update.mutate(finalState, {
      onSuccess: () => {
        updateCallRouteCaches({
          voicemailBoxName: finalState.name,
          sharedAccess: finalState.sharedAccess,
          sendNotification: finalState.sendNotification,
          emailAddresses: finalState.emailAddresses,
          smsNumbers: finalState.smsNumbers,
        });

        alert.success(t('Voicemail Box updated successfully.'));

        invalidateQueries([settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listVoicemailBox()]);
        invalidateQueries([
          settingsTenantLocation?.phoneTenantId,
          ...queryKeys.settings.readVoicemailBox(voicemailBoxId),
        ]);

        modalControls.closeModal();
      },
      onError: () => {
        alert.error(t('System failed to update Voicemail Box. Please try again.'));
      },
    });
  }, [
    voicemailBoxData,
    form.values,
    update,
    updateCallRouteCaches,
    alert,
    t,
    invalidateQueries,
    settingsTenantLocation,
    voicemailBoxId,
    modalControls,
  ]);

  const handleCancel = useCallback(() => {
    modalControls.closeModal();
  }, [modalControls]);

  if (!settingsTenantLocation) {
    return null;
  }

  return (
    <>
      <Tray.Header>{t('Edit Voicemail Box')}</Tray.Header>
      <Tray.Body>
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Settings')}
        </Heading>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          {!!usageData?.length && (
            <BannerNotification
              status='info'
              message={
                <Trans t={t} count={usageData.length}>
                  This Voicemail Box is being used in{' '}
                  <TextLink as='span' href='#' css={{ display: 'inline' }} onClick={usageTriggerProps.onClick}>
                    <span>{{ count: usageData.length }}</span> different step
                  </TextLink>{' '}
                  in your Call Routes. Any changes you make here will be reflected in all of those places.
                </Trans>
              }
            />
          )}

          <TextField {...nameField} label={t('Voicemail Box Name')} name='name' css={{ maxWidth: theme.spacing(40) }} />

          <VoicemailBoxContent
            data={voicemailBoxData}
            tenantLocation={settingsTenantLocation}
            form={form}
            hasPhoneFullAccess={true}
            tableLayout='combined'
          />

          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(2)};
              justify-content: flex-end;
            `}
          >
            <Button size='large' variant='secondary' onClick={handleCancel}>
              {t('Cancel')}
            </Button>
            <Button
              size='large'
              css={{ width: '62px' }}
              disabled={!form.changedValues || update.isLoading || notificationsEnabledWithoutContacts}
              variant='primary'
              onClick={handleSave}
              loading={update.isLoading}
              trackingId='phone::setting::call-route:save-voicemailbox-btn'
              {...(notificationsEnabledWithoutContacts ? saveButtonTriggerProps : {})}
            >
              {t('Save')}
            </Button>
          </div>
          {NotificationTooltip}
        </div>
      </Tray.Body>

      <VoicemailBoxUsageModal
        modalProps={usageModalProps}
        usageData={usageData || []}
        modalControls={modalControls}
        addStepModalProps={addStepModalProps}
        stepContext={stepContext}
      />
    </>
  );
};

export const VoicemailSettingTray = ({
  addStepModalProps,
  modalControls,
  mountTarget,
  selectedVoicemailBox,
  stepContext,
}: Props) => {
  const { t } = useTranslation('phone');
  return (
    <Tray
      mountTarget={mountTarget}
      width='xlarge'
      zIndex={10001}
      css={css`
        padding: ${theme.spacing(4)};
        border-top: 1px solid ${theme.colors.neutral20};
        overflow-y: auto;
      `}
      {...modalControls.modalProps}
    >
      {modalControls.modalProps.show && selectedVoicemailBox?.mailbox?.id && (
        <VoicemailSettingTrayContent
          selectedVoicemailBox={selectedVoicemailBox}
          modalControls={modalControls}
          addStepModalProps={addStepModalProps}
          stepContext={stepContext}
        />
      )}
      {modalControls.modalProps.show && !selectedVoicemailBox?.mailbox?.id && (
        <div css={{ padding: theme.spacing(4), textAlign: 'center' }}>
          <Text>{t('Loading voicemail box details...')}</Text>
        </div>
      )}
    </Tray>
  );
};

const VoicemailBoxUsageModal = ({
  modalProps,
  usageData,
  modalControls,
  addStepModalProps,
  stepContext,
}: {
  modalProps: ModalControlModalProps;
  usageData: CallRouteUsage[];
  modalControls: ModalControlResponse;
  addStepModalProps?: ModalControlModalProps;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
}) => {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();

  return (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>{t('Voicemail Box Usage')}</Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Text>{t('This Voicemail Box is currently assigned to the following Call Routes:')}</Text>
          <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {usageData.map(({ callRouteId, callRouteName, usageCount }) => (
              <div key={callRouteId} css={{ display: 'flex', gap: '4px', alignItems: 'center', width: '500px' }}>
                <TextLink
                  href='#'
                  onClick={() => {
                    modalControls.closeModal();
                    addStepModalProps?.onClose?.();
                    stepContext?.onClose?.();
                    navigate({ to: '/phone/call-routes/:id', params: { id: callRouteId } });
                  }}
                >
                  {callRouteName}
                </TextLink>
                {usageCount !== undefined && usageCount > 1 && (
                  <Text size='small' color='subdued'>
                    ({t('{{count}} times', { count: usageCount })})
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Close'),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
